import { useMemo, useState } from 'react';

import { Check, ChevronsUpDown } from 'lucide-react';

import { Button } from '@/shadcn/button';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/shadcn/command';
import { cn } from '@/shadcn/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/shadcn/popover';

import { NAVBAR_NARROW_HIDDEN } from '@/components/layouts/header/navbar.constants';
import { Ellipsis } from '@/components/ui/ellipsis/ellipsis';
import { ProductionBadge } from './ProductionBadge';
import { WorkspaceTag } from './WorkspaceTag';
import type { WorkspaceFilterSelection } from './workflowCards.schema';
import {
  groupWorkspaces,
  type QualifyingWorkspace
} from './workspaceFilterMatch';

type Props = {
  workspaces: QualifyingWorkspace[];
  selection: WorkspaceFilterSelection;
  userId: string;
  onChange: (next: WorkspaceFilterSelection) => void;
};

export const WorkspaceFilter = ({
  workspaces,
  selection,
  userId,
  onChange
}: Props) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const isProduction = selection.kind === 'production';
  const selectedId = selection.kind === 'workspace' ? selection.id : null;
  const selectedWorkspace = selectedId
    ? workspaces.find(w => w.rowId === selectedId)
    : null;

  const { mine, others } = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    const filtered = trimmed
      ? workspaces.filter(w => w.name.toLowerCase().includes(trimmed))
      : workspaces;
    return groupWorkspaces(filtered, userId);
  }, [workspaces, query, userId]);

  const select = (next: WorkspaceFilterSelection) => {
    onChange(next);
    setOpen(false);
  };

  const renderWorkspaceItem = (ws: QualifyingWorkspace) => (
    <CommandItem
      data-cy={`workspace-filter-item-${ws.rowId}`}
      data-testid={`workspace-filter-item-${ws.rowId}`}
      key={ws.rowId}
      onSelect={() => select({ kind: 'workspace', id: ws.rowId })}
      value={ws.rowId}
    >
      <Check
        className={cn(
          'mr-2 h-4 w-4 shrink-0',
          selectedId === ws.rowId ? 'opacity-100' : 'opacity-0'
        )}
      />
      <WorkspaceTag
        alignBadgeEnd
        color={ws.color}
        name={ws.name}
        version={ws.version}
      />
    </CommandItem>
  );

  const hasWorkspaceResults = mine.length > 0 || others.length > 0;

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <Button
          className="h-8 min-w-0 max-w-96 justify-between gap-2 rounded-lg border border-border bg-card font-normal text-xs normal-case leading-normal tracking-normal active:scale-100"
          data-cy="workspace-filter-trigger"
          data-testid="workspace-filter-trigger"
          variant="outline"
        >
          {selectedWorkspace ? (
            <WorkspaceTag
              badgeClassName={NAVBAR_NARROW_HIDDEN}
              color={selectedWorkspace.color}
              name={selectedWorkspace.name}
              version={selectedWorkspace.version}
            />
          ) : (
            <ProductionBadge />
          )}
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-96 p-0">
        <Command shouldFilter={false}>
          <CommandInput
            onValueChange={setQuery}
            placeholder="Filter workspaces…"
            value={query}
          />
          <CommandList>
            <CommandGroup>
              <CommandItem
                data-cy="workspace-filter-production"
                data-testid="workspace-filter-production"
                onSelect={() => select({ kind: 'production' })}
                value="__production__"
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4 shrink-0',
                    isProduction ? 'opacity-100' : 'opacity-0'
                  )}
                />
                <div className="flex min-w-0 grow items-center gap-1.5">
                  <span
                    aria-hidden
                    className="size-2.5 shrink-0 rounded-full bg-muted-foreground"
                  />
                  <Ellipsis className="min-w-0 text-sm">No workspace</Ellipsis>
                  <ProductionBadge className="ml-auto" />
                </div>
              </CommandItem>
            </CommandGroup>
            {mine.length > 0 && (
              <CommandGroup heading="My workspaces">
                {mine.map(renderWorkspaceItem)}
              </CommandGroup>
            )}
            {others.length > 0 && (
              <CommandGroup heading="Workspaces">
                {others.map(renderWorkspaceItem)}
              </CommandGroup>
            )}
            {!hasWorkspaceResults && (
              <div className="py-4 text-center text-muted-foreground text-sm">
                No workspace found.
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
